import { FlexRow } from '@/components/common/Flex';
import { useOnboardingPosition } from '@/components/onboarding/useOnboardingPosition';
import { uploadFile } from '@/services/upload';
import { useUserData } from '@/services/user';
import { isDev } from '@/util/env';
import { openInNewTab } from '@/util/url';
import { useMutation } from '@apollo/client';
import { FlexProps, Spacer, Textarea } from '@chakra-ui/react';
import { BOOK_ONBOARDING_CALL_CALENDLY_URL } from '@common/calendly';
import {
    CreateUserFeedbackDocument,
    CurrentUserProfileDocument,
} from '@common/graphql/generated';
import { TOASTS } from '@common/toasts';
import { useRouter } from 'next/router';
import { useRef, useState } from 'react';
import Balancer from 'react-wrap-balancer';
import { ArrowLinkText } from '../common/ArrowLinkText';
import { Button, ButtonProps, IconButton } from '../common/Button';
import FadeInBox from '../common/FadeInBox';
import { XClose } from '../common/icons';
import { useErrorToast } from '../common/Toasts';
import { Paragraph } from '../common/Typography';
import AttachmentsInput from './AttachmentsInput';

type FeedbackWidgetStep = 'CLOSED' | 'OPEN' | 'SUBMITTED';

const FEEDBACK_WIDGET_Z_INDEX = 900;

const FeedbackWidget = ({ isFixed }: { isFixed: boolean }) => {
    const router = useRouter();
    const { userInfo } = useUserData();
    const [step, setStep] = useState<FeedbackWidgetStep>('CLOSED');
    const [body, setBody] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [createUserFeedback] = useMutation(CreateUserFeedbackDocument, {
        refetchQueries: [CurrentUserProfileDocument],
    });
    const showErrorToast = useErrorToast();
    const onboardingPosition = useOnboardingPosition();

    if (!userInfo || userInfo.isAdmin) return null;

    const submit = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            // Gather input files into an array
            const files =
                fileInputRef.current?.files &&
                fileInputRef.current.files.length > 0
                    ? Array.from(fileInputRef.current.files)
                    : [];

            // Upload attached file(s) to S3 (except during development)
            const attachments =
                !isDev && files.length > 0
                    ? await Promise.all(files.map((file) => uploadFile(file)))
                    : undefined;

            // Save feedback body and attachment metadata via GraphQL
            await createUserFeedback({
                variables: {
                    body,
                    attachments,
                    platform: 'WEB',
                },
            });

            // Clear body and show success screen
            setBody('');
            setStep('SUBMITTED');
        } catch (err) {
            showErrorToast(TOASTS.createUserFeedbackError);
            console.warn(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const bookOnboardingCall = () => {
        openInNewTab(BOOK_ONBOARDING_CALL_CALENDLY_URL);
    };

    const feedbackButtonLabel =
        onboardingPosition === 'complete' && router.pathname !== '/onboarding'
            ? 'Message Plenty'
            : 'Book a free onboarding call';

    return (
        <>
            {step === 'CLOSED' && (
                <FeedbackButton
                    {...(isFixed && { position: 'fixed', bottom: 4, right: 4 })}
                    onClick={() => {
                        if (onboardingPosition === 'complete') {
                            setStep('OPEN');
                        } else {
                            bookOnboardingCall();
                        }
                    }}
                    onClickEvent={
                        onboardingPosition === 'complete'
                            ? 'Feedback Widget Opened'
                            : 'Book Onboarding Call Button Clicked'
                    }
                    zIndex={FEEDBACK_WIDGET_Z_INDEX}
                >
                    {feedbackButtonLabel}
                </FeedbackButton>
            )}
            {step === 'OPEN' && (
                <FeedbackLayout key={step} onClose={() => setStep('CLOSED')}>
                    <Paragraph
                        fontSize="md"
                        color="textMuted"
                        pt={1}
                        pl={2}
                        pr={7}
                    >
                        <Balancer>
                            {
                                'Have a question or feedback? You’ll hear from us soon.'
                            }
                            <br />
                            {'Or '}
                            <ArrowLinkText
                                fontSize="md"
                                onClick={bookOnboardingCall}
                            >
                                book an onboarding call
                            </ArrowLinkText>
                        </Balancer>
                    </Paragraph>

                    <FlexRow grow={1} px={2}>
                        <Textarea
                            placeholder={'Write your message here'}
                            size="md"
                            resize="none"
                            h="full"
                            value={body}
                            onChange={(e) => {
                                setBody(e.target.value);
                            }}
                            autoFocus
                        />
                    </FlexRow>
                    <FlexRow alignItems="center" gap={2}>
                        <AttachmentsInput fileInputRef={fileInputRef} />
                        <Spacer />
                        <FeedbackButton
                            onClick={() => submit()}
                            onClickEvent="Feedback Submitted"
                            isLoading={isSubmitting}
                            isDisabled={body.trim() === ''}
                            px={6}
                        >
                            Send
                        </FeedbackButton>
                    </FlexRow>
                </FeedbackLayout>
            )}
            {step === 'SUBMITTED' && (
                <FeedbackLayout key={step} onClose={() => setStep('CLOSED')}>
                    <FlexRow justifyContent="flex-end" p={4}></FlexRow>
                    <FlexRow grow={1} alignItems="center" px={6}>
                        <Paragraph color="textMuted">
                            <Balancer>
                                {
                                    'Thank you for messaging. You’ll hear from us soon.'
                                }
                            </Balancer>
                        </Paragraph>
                    </FlexRow>
                    <FlexRow justifyContent="flex-end" p={2}>
                        <FeedbackButton
                            onClick={() => setStep('OPEN')}
                            onClickEvent="More Feedback Button Clicked"
                        >
                            Leave more feedback
                        </FeedbackButton>
                    </FlexRow>
                </FeedbackLayout>
            )}
        </>
    );
};

export default FeedbackWidget;

interface FeedbackButtonProps extends ButtonProps {}

const FeedbackButton = ({ children, ...props }: FeedbackButtonProps) => {
    return (
        <Button
            size="xs"
            fontSize="sm"
            colorScheme="dark"
            bg="black"
            py={{ base: 2, sm: 4 }}
            {...props}
        >
            {children}
        </Button>
    );
};

interface FeedbackLayoutProps extends FlexProps {
    onClose: VoidFunction;
}

const FeedbackLayout = ({
    onClose,
    children,
    ...props
}: FeedbackLayoutProps) => {
    return (
        <FlexRow
            bg="white"
            position="fixed"
            bottom={2}
            right={2}
            w={300}
            h={400}
            rounded="md"
            boxShadow="lg"
            border="1px solid"
            borderColor="blackAlpha.100"
            zIndex={FEEDBACK_WIDGET_Z_INDEX}
        >
            <IconButton
                size="xs"
                variant="ghost"
                colorScheme="gray"
                icon={<XClose />}
                aria-label="close"
                onClick={onClose}
                onClickEvent="Close Feedback Widget Button Clicked"
                position="absolute"
                top={4}
                right={4}
            />
            <FadeInBox
                animate={{
                    opacity: 1,
                    transition: {
                        duration: 0.25, // faster than default
                    },
                }}
                h="full"
                w="full"
                direction="column"
                p={4}
                gap={4}
                {...props}
            >
                {children}
            </FadeInBox>
        </FlexRow>
    );
};
